import { useUpdatePropNew } from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Edit02Icon } from 'src/editor/shared/icons/edit-02-icon'
import {
  ButtonSizeEnum,
  GhostIconButton,
} from 'src/editor/shared/ui-lib/buttons'
import { getEntityNameLocoKeyByEntityType } from 'src/editor/shared/utils/get-entity-name-loco-key-by-entity-type'
import { twMerge } from 'tailwind-merge'
import { FormInterface } from 'src/shared/types/entities/form-interface'

interface EntityCustomNameInputProps {
  entity: FormInterface
}

export const EntityCustomNameInput = ({
  entity,
}: EntityCustomNameInputProps) => {
  const { t } = useTranslation()

  const inputRef = useRef<HTMLInputElement>(null)

  const [customNameState, setCustomNameState] = useState(entity.customName)
  const [isEditing, setIsEditing] = useState(false)
  const updateCustomNameProp = useUpdatePropNew(entity)('customName')

  useEffect(() => {
    if (!isEditing) {
      if (customNameState?.trim()) {
        updateCustomNameProp(customNameState)
      } else {
        updateCustomNameProp(t(getEntityNameLocoKeyByEntityType(entity.type)))
      }
    } else {
      inputRef.current?.focus()
    }
  }, [isEditing])

  useEffect(() => {
    setCustomNameState(entity.customName)
  }, [entity.customName])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isEditing) {
        if (e.key === 'Enter') {
          setIsEditing(false)
        } else if (e.key === 'Escape') {
          // NOTE: revert changes if user presses Escape
          setCustomNameState(entity.customName)
          setIsEditing(false)
        }
      }
    }
    inputRef.current?.addEventListener('keydown', handleKeyDown)
    return () => {
      inputRef.current?.removeEventListener('keydown', handleKeyDown)
    }
  }, [isEditing])

  const onChange = (value: string) => {
    setCustomNameState(value)
  }

  return (
    <div
      className={'group relative w-full overflow-hidden hover:overflow-visible'}
    >
      <input
        ref={inputRef}
        className={twMerge(
          'w-full text-ellipsis border-1 border-transparent text-heading-4',
          'focus:border-b-inputBorder focus:outline-none',
          !isEditing && 'selection:bg-transparent',
        )}
        type="text"
        title={customNameState}
        value={customNameState}
        onChange={e => onChange(e.target.value)}
        disabled={!isEditing}
        onBlur={() => setIsEditing(false)}
      />
      <GhostIconButton
        className={twMerge(
          'absolute top-[-3px] right-[-100%] opacity-0 transition-opacity',
          '!bg-bgPrimaryBase hover:bg-btnGhostPrimaryHover!',
          !isEditing && 'group-hover:right-0 group-hover:opacity-100',
        )}
        size={ButtonSizeEnum.Small}
        icon={<Edit02Icon />}
        onClick={() => setIsEditing(true)}
      />
    </div>
  )
}
